import { useEffect, useState } from 'react';
import { cloudApi } from '../services/api';
import {
  CLOUD_FOLDER_PLACEHOLDER,
  CLOUD_OAUTH_STUB_NOTE,
  CLOUD_PROVIDERS,
  CLOUD_STORAGE_HELPER_LINE,
  buildCloudStorageMeta,
  isLiveCloudProvider,
  isGoogleCloudProvider,
  isStubCloudProvider,
  normalizeCloudProvider,
  type CloudProviderValue,
  type CloudStorageMeta,
} from '../lib/cloudStorage';
import './FileStoragePanel.css';

const STORAGE_PREFIX = 'mentology.cloudStorage.';

export function loadCloudStorageIntent(storageKey: string): CloudStorageMeta {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (!raw) return buildCloudStorageMeta({ provider: 'pending', folder_label: '' });
    const parsed = JSON.parse(raw) as Partial<CloudStorageMeta>;
    return buildCloudStorageMeta(parsed);
  } catch {
    return buildCloudStorageMeta({ provider: 'pending', folder_label: '' });
  }
}

export function saveCloudStorageIntent(storageKey: string, meta: CloudStorageMeta) {
  localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, JSON.stringify(meta));
}

interface FileStoragePanelProps {
  storageKey: string;
  scope?: 'user' | 'org';
  returnTo?: string;
  /** Optional: also push metadata to an API (e.g. user bio / org cloud). */
  onPersist?: (meta: CloudStorageMeta) => Promise<void> | void;
  compact?: boolean;
  defaultOpen?: boolean;
  folderPlaceholder?: string;
  folderLabelHeading?: string;
}

export default function FileStoragePanel({
  storageKey,
  scope = 'user',
  returnTo,
  onPersist,
  compact = false,
  defaultOpen = false,
  folderPlaceholder = CLOUD_FOLDER_PLACEHOLDER,
  folderLabelHeading = 'Business files folder label',
}: FileStoragePanelProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [provider, setProvider] = useState<CloudProviderValue>('pending');
  const [folderLabel, setFolderLabel] = useState('');
  const [folderId, setFolderId] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [folders, setFolders] = useState<Array<{ id: string; name: string }>>([]);
  const [googleConfigured, setGoogleConfigured] = useState(false);

  const refreshStatus = async () => {
    try {
      const data = await cloudApi.status(scope);
      setGoogleConfigured(Boolean(data.googleConfigured));
      const cloud = buildCloudStorageMeta(data.cloud_storage || {});
      setProvider(cloud.provider);
      setFolderLabel(cloud.folder_label);
      setFolderId(cloud.folder_id || null);
      setConnected(Boolean(cloud.connected));
      saveCloudStorageIntent(storageKey, cloud);
    } catch {
      const saved = loadCloudStorageIntent(storageKey);
      setProvider(saved.provider);
      setFolderLabel(saved.folder_label);
      setFolderId(saved.folder_id || null);
      setConnected(Boolean(saved.connected));
    }
  };

  useEffect(() => {
    void refreshStatus();
  }, [storageKey, scope]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cloud = params.get('cloud');
    if (!cloud) return;
    if (cloud === 'connected') setStatus('Google Drive connected. Folder ready for Tier 2 files.');
    if (cloud === 'denied') setStatus('Google authorization was denied.');
    if (cloud === 'error' || cloud === 'invalid_state') {
      setStatus('Google connection failed. Try Connect Google Drive again.');
    }
    void refreshStatus();
    params.delete('cloud');
    const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
    window.history.replaceState({}, '', next);
  }, []);

  const handleSaveIntent = async () => {
    setSaving(true);
    setStatus('');
    const meta = buildCloudStorageMeta({
      provider: normalizeCloudProvider(provider),
      folder_label: folderLabel,
      folder_id: isLiveCloudProvider(provider) ? folderId : null,
      connected: isLiveCloudProvider(provider) ? connected : false,
    });
    try {
      saveCloudStorageIntent(storageKey, meta);
      await onPersist?.(meta);
      if (isStubCloudProvider(meta.provider)) {
        setStatus('Provider intent saved. Live OAuth for this provider is not registered yet — connected stays false.');
      } else if (meta.provider === 'pending') {
        setStatus('Connect later saved. Setup can continue without cloud access.');
      } else if (meta.connected) {
        setStatus('Google Drive folder metadata saved (Tier 2 pointers only).');
      } else {
        setStatus('Google Drive selected. Use Connect Google Drive to complete OAuth.');
      }
      setProvider(meta.provider);
      setFolderLabel(meta.folder_label);
      setFolderId(meta.folder_id || null);
      setConnected(Boolean(meta.connected));
    } catch (error) {
      console.warn('[FileStoragePanel]', error);
      setStatus('Saved locally. Server sync pending.');
    } finally {
      setSaving(false);
    }
  };

  const handleConnectGoogle = async () => {
    setStatus('');
    try {
      const data = await cloudApi.startGoogleOAuth({
        scope,
        returnTo: returnTo || window.location.pathname,
        folderLabel: folderLabel || folderPlaceholder.replace(/^e\.g\.\s*/i, ''),
      });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      setStatus('Unable to start Google OAuth.');
    } catch (error: unknown) {
      const err = error as { error?: string };
      setStatus(err.error || 'Google OAuth is not configured on the server.');
    }
  };

  const handleDisconnect = async () => {
    setSaving(true);
    try {
      const data = await cloudApi.disconnectGoogle(scope);
      const cloud = buildCloudStorageMeta(data.cloud_storage || { provider: 'pending' });
      setProvider(isGoogleCloudProvider(cloud.provider) ? 'pending' : cloud.provider);
      setFolderId(null);
      setConnected(false);
      saveCloudStorageIntent(storageKey, { ...cloud, connected: false, folder_id: null });
      await onPersist?.({ ...cloud, connected: false, folder_id: null, provider: 'pending' });
      setStatus(data.message || 'Google Drive disconnected.');
      setFolders([]);
    } catch (error: unknown) {
      const err = error as { error?: string };
      setStatus(err.error || 'Disconnect failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadFolders = async () => {
    try {
      const data = await cloudApi.listFolders(scope, folderLabel);
      setFolders(data.folders || []);
      if (!(data.folders || []).length) {
        setStatus('No matching folders found. Connect Google Drive first or adjust the folder label.');
      }
    } catch (error: unknown) {
      const err = error as { error?: string; connected?: boolean };
      if (err.connected === false) {
        setConnected(false);
        setFolderId(null);
      }
      setStatus(err.error || 'Unable to list Drive folders.');
    }
  };

  const handleSelectFolder = async (id: string, name: string) => {
    setSaving(true);
    try {
      const data = await cloudApi.saveFolder({
        scope,
        folder_id: id,
        folder_label: name || folderLabel,
      });
      const cloud = buildCloudStorageMeta(data.cloud_storage || {});
      setFolderId(cloud.folder_id || id);
      setFolderLabel(cloud.folder_label || name);
      setConnected(Boolean(cloud.connected));
      setProvider('google_drive');
      saveCloudStorageIntent(storageKey, cloud);
      await onPersist?.(cloud);
      setStatus(`Folder selected: ${cloud.folder_label}`);
    } catch (error: unknown) {
      const err = error as { error?: string };
      setStatus(err.error || 'Unable to save folder.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`file-storage-panel ${compact ? 'file-storage-panel-compact' : ''}`}>
      <button
        type="button"
        className="file-storage-toggle btn-primary"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        File Storage {open ? '▲' : '▼'}
      </button>

      {open && (
        <div className="file-storage-body">
          <p className="file-storage-note">{CLOUD_OAUTH_STUB_NOTE}</p>
          <p className="file-storage-helper">
            Status: {connected ? 'Connected to Google Drive' : 'Not connected'}
            {folderId ? ` · folder id saved` : ''}
            {googleConfigured ? '' : ' · Google OAuth env not configured on server'}
          </p>

          <label className="file-storage-label">
            Cloud storage (Tier 2 — your folder)
            <select
              className="input-field file-storage-input"
              value={provider}
              onChange={(e) => {
                const next = normalizeCloudProvider(e.target.value);
                setProvider(next);
                if (!isGoogleCloudProvider(next)) {
                  setConnected(false);
                  setFolderId(null);
                }
              }}
            >
              {CLOUD_PROVIDERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="file-storage-label">
            {folderLabelHeading}
            <input
              className="input-field file-storage-input"
              type="text"
              value={folderLabel}
              placeholder={folderPlaceholder}
              onChange={(e) => setFolderLabel(e.target.value)}
            />
          </label>

          <p className="file-storage-helper">{CLOUD_STORAGE_HELPER_LINE}</p>

          {isGoogleCloudProvider(provider) && (
            <div className="file-storage-google-actions">
              {!connected ? (
                <button
                  type="button"
                  className="btn-primary file-storage-save"
                  onClick={() => void handleConnectGoogle()}
                  disabled={!googleConfigured || saving}
                >
                  Connect Google Drive
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn-primary file-storage-save"
                    onClick={() => void handleLoadFolders()}
                    disabled={saving}
                  >
                    Browse / confirm Drive folders
                  </button>
                  <button
                    type="button"
                    className="btn-primary file-storage-save"
                    onClick={() => void handleDisconnect()}
                    disabled={saving}
                  >
                    Disconnect Google Drive
                  </button>
                </>
              )}
              {folders.length > 0 && (
                <ul className="file-storage-folder-list">
                  {folders.map((folder) => (
                    <li key={folder.id}>
                      <button
                        type="button"
                        className="file-storage-folder-btn"
                        onClick={() => void handleSelectFolder(folder.id, folder.name)}
                      >
                        {folder.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <button
            type="button"
            className="btn-primary file-storage-save"
            onClick={() => void handleSaveIntent()}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save File Storage'}
          </button>
          {status && <p className="file-storage-status">{status}</p>}
        </div>
      )}
    </div>
  );
}
